import { useCallback, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import { Upload, X, Loader2, Move, Check } from "lucide-react";
import { api } from "../../lib/api";

/**
 * ImageCropAndFocal
 * --------------------------------------------------------------
 * Reusable CMS uploader for "headshot" style photos.
 *
 *  1. Editor picks a file (or drops it).
 *  2. A modal opens with react-easy-crop locked to 1:1 (square).
 *     They drag/zoom to frame the face, hit "Recortar y subir".
 *  3. The cropped Blob is uploaded to /api/admin/upload.
 *  4. The saved photo is then shown with a small focal-point
 *     selector — the editor clicks the spot that should remain
 *     centered when the image is later cropped by the layout
 *     (e.g. aspect-square vs aspect-[4/5]).
 *
 * Props:
 *   value      → string (current photo URL)
 *   onChange   → (url) => void
 *   focalX     → number 0-100
 *   focalY     → number 0-100
 *   onFocalChange → ({focal_x, focal_y}) => void
 */
export default function ImageCropAndFocal({
  value,
  onChange,
  focalX = 50,
  focalY = 50,
  onFocalChange,
  label = "Foto",
  help,
}) {
  const inputRef = useRef(null);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [cropSrc, setCropSrc] = useState(null); // dataURL shown in the crop modal
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedArea, setCroppedArea] = useState(null);

  const onCropComplete = useCallback((_, areaPixels) => {
    setCroppedArea(areaPixels);
  }, []);

  const onFile = (file) => {
    if (!file) return;
    setError("");
    const reader = new FileReader();
    reader.onload = () => {
      setCropSrc(reader.result);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    };
    reader.readAsDataURL(file);
  };

  const handleConfirmCrop = async () => {
    if (!cropSrc || !croppedArea) return;
    setUploading(true);
    setError("");
    try {
      const blob = await getCroppedBlob(cropSrc, croppedArea);
      const fd = new FormData();
      fd.append("file", blob, "headshot.jpg");
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Save the RELATIVE URL (`/api/files/...`) — see ImageUpload.jsx for
      // why (portability across preview/prod environments).
      onChange(data.url);
      // Re-center focal point on every new upload — they can fine-tune after.
      onFocalChange?.({ focal_x: 50, focal_y: 50 });
      setCropSrc(null);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Error al subir");
    } finally {
      setUploading(false);
    }
  };

  const handleFocalClick = (e) => {
    if (!onFocalChange) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
    onFocalChange({ focal_x: Math.round(x), focal_y: Math.round(y) });
  };

  return (
    <div data-testid="image-crop-focal">
      {label && <label className="block text-xs font-medium text-gray-700 mb-1 uppercase tracking-wider">{label}</label>}

      {value ? (
        <div className="bg-gray-50 border border-gray-200 p-4 space-y-4">
          {/* ---- Focal point picker ---- */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-medium text-gray-700 mb-2 uppercase tracking-wider flex items-center gap-1.5">
                <Move className="w-3 h-3" /> Punto focal
              </p>
              <div
                onClick={handleFocalClick}
                data-testid="focal-picker"
                className="relative w-full aspect-square bg-white border border-gray-200 cursor-crosshair overflow-hidden select-none"
                title="Haga clic sobre la cara"
              >
                <img src={value} alt="" className="w-full h-full object-contain" draggable={false} />
                <div
                  className="absolute w-6 h-6 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-lg bg-[#C8102E] pointer-events-none"
                  style={{ left: `${focalX}%`, top: `${focalY}%` }}
                  data-testid="focal-marker"
                />
              </div>
              <p className="text-[11px] text-gray-500 mt-2">
                Haga clic sobre la cara. Coordenadas: {focalX}% × {focalY}%
              </p>
            </div>

            <div>
              <p className="text-xs font-medium text-gray-700 mb-2 uppercase tracking-wider">Vista previa pública</p>
              <div className="w-full aspect-square bg-gray-100 overflow-hidden border border-gray-200">
                <img
                  src={value}
                  alt=""
                  className="w-full h-full object-cover"
                  style={{ objectPosition: `${focalX}% ${focalY}%` }}
                />
              </div>
              <p className="text-[11px] text-gray-500 mt-2">
                Así se verá en la tarjeta del equipo.
              </p>
            </div>
          </div>

          <div className="flex gap-2 pt-3 border-t border-gray-100">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="text-xs px-3 py-1.5 border border-gray-200 hover:border-[#0F2040] inline-flex items-center gap-1.5"
              data-testid="upload-replace"
            >
              <Upload className="w-3 h-3" /> Reemplazar foto
            </button>
            <button
              type="button"
              onClick={() => {
                onChange("");
                onFocalChange?.({ focal_x: 50, focal_y: 50 });
              }}
              className="text-xs px-3 py-1.5 border border-gray-200 hover:border-red-500 hover:text-red-600 inline-flex items-center gap-1.5"
              data-testid="upload-clear"
            >
              <X className="w-3 h-3" /> Quitar
            </button>
          </div>
        </div>
      ) : (
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            onFile(e.dataTransfer.files?.[0]);
          }}
          onClick={() => inputRef.current?.click()}
          className="border-2 border-dashed border-gray-300 hover:border-[#0F2040] cursor-pointer p-8 text-center transition-colors"
          data-testid="upload-dropzone"
        >
          <Upload className="w-8 h-8 text-gray-400 mx-auto mb-3" strokeWidth={1.4} />
          <p className="text-sm font-medium text-[#0F2040]">Arrastre la foto aquí o haga clic para seleccionar</p>
          <p className="text-xs text-gray-500 mt-1">Se recortará a 1:1 antes de subir · JPG, PNG, WebP</p>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          onFile(e.target.files?.[0]);
          e.target.value = "";
        }}
        data-testid="upload-input"
      />

      {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
      {help && <p className="text-xs text-gray-500 mt-2">{help}</p>}

      {/* -------- Crop modal -------- */}
      {cropSrc && (
        <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4" data-testid="crop-modal">
          <div className="bg-white max-w-2xl w-full">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
              <h3 className="font-semibold text-[#0F2040]">Recortar foto</h3>
              <button type="button" onClick={() => setCropSrc(null)} disabled={uploading}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="relative w-full h-[420px] bg-black">
              <Cropper
                image={cropSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>
            <div className="px-6 py-4 space-y-3 border-t border-gray-100">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1 uppercase tracking-wider">Zoom</label>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.01}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="w-full accent-[#C8102E]"
                  data-testid="crop-zoom"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setCropSrc(null)}
                  disabled={uploading}
                  className="px-4 py-2 text-sm border border-gray-200 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleConfirmCrop}
                  disabled={uploading}
                  data-testid="crop-confirm"
                  className="bg-[#C8102E] text-white px-4 py-2 text-sm flex items-center gap-2 hover:bg-[#A30D25] disabled:opacity-60"
                >
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  Recortar y subir
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Crop a region of `imageSrc` and return a JPEG Blob.
 * Output is bounded to 800×800 to keep payloads light.
 */
async function getCroppedBlob(imageSrc, area) {
  const image = await loadImage(imageSrc);
  const targetSize = Math.min(800, Math.round(area.width));
  const canvas = document.createElement("canvas");
  canvas.width = targetSize;
  canvas.height = targetSize;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(
    image,
    area.x,
    area.y,
    area.width,
    area.height,
    0,
    0,
    targetSize,
    targetSize
  );
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Canvas vacío"))),
      "image/jpeg",
      0.85
    );
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
